import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGet, entitlementState } from '../utils/api';
import { LockedFeaturePanel, LimitReachedNotice } from '../components/EntitlementStates';
import ContractModal from '../components/ContractModal';
import NegotiationModal from '../components/NegotiationModal';
import MarketDealModal from '../components/MarketDealModal';
import CommunicationsTimeline from '../components/CommunicationsTimeline';
import DealDocuments from '../components/DealDocuments';
import CompMap from '../components/CompMap';
import CompDetailModal from '../components/CompDetailModal';
import InteractiveDealCalc from '../components/InteractiveDealCalc';
import DealPacketModal from '../components/DealPacketModal';
import StageBadge from '../components/StageBadge';
import { fmtLotSize, yesNoUnknown, fmtHoa, taxRecordsLink } from '../utils/propertyFacts';
import DealEditModal from '../components/DealEditModal';
import { proxyPhoto } from '../utils/photo';

// Tools nav across the deal page. Each key maps to a panel below the material facts + photos.
const DEAL_TABS = [
  { key: 'arv',   label: 'ARV Estimator' },
  { key: 'rehab', label: 'Rehab Estimator' },
  { key: 'mao',   label: 'MAO Calculator' },
  { key: 'roi',   label: 'Investor ROI' },
  { key: 'comms', label: 'Communications' },
  { key: 'docs',  label: 'Documents' },
];

// Photos live on the deal as a JSON array (`photos`) plus a single `photo_url`. Merge + de-dupe.
function parsePhotos(deal) {
  let arr = [];
  if (Array.isArray(deal.photos)) arr = deal.photos;
  else if (typeof deal.photos === 'string') { try { const p = JSON.parse(deal.photos); if (Array.isArray(p)) arr = p; } catch { /* not JSON */ } }
  if (deal.photo_url) arr = [deal.photo_url, ...arr];
  return [...new Set(arr.filter(Boolean))];
}

// Description/property-type heuristic — only ever UPGRADES an unknown to 'Yes' (never a false 'No'),
// so a not-yet-resynced listing still surfaces a clear pool/HOA mention instead of "Unknown".
function heuristicYes(deal, kind) {
  const txt = `${deal.description || ''} ${deal.property_type || ''} ${deal.remarks || ''}`.toLowerCase();
  if (kind === 'pool') return /\bpool\b/.test(txt) && !/\bno pool\b|without pool/.test(txt);
  return /\bhoa\b|homeowners?\s+assoc/.test(txt);
}

// Pool: real MLS tri-state first (deal.pool = 1/0/null), else the heuristic can upgrade Unknown→Yes.
function poolFact(deal) {
  if (deal.pool != null) return yesNoUnknown(deal.pool);
  return heuristicYes(deal, 'pool') ? 'Yes' : 'Unknown';
}

// HOA: real MLS tri-state + fee first, else the heuristic can upgrade Unknown→Yes.
function hoaFact(deal) {
  if (deal.hoa != null) return fmtHoa(deal);
  return heuristicYes(deal, 'hoa') ? 'Yes' : 'Unknown';
}

function fmtMoney(n) {
  if (!n && n !== 0) return '—';
  return `$${n.toLocaleString()}`;
}

function fmtK(n) {
  if (!n) return '—';
  return `$${Math.round(n / 1000)}k`;
}

function Section({ title, children }) {
  return (
    <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: '24px', marginBottom: 20 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)', marginBottom: 20, textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.85 }}>{title}</h3>
      {children}
    </div>
  );
}

function MetricRow({ label, value, highlight, sub }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(232, 227, 218, 0.7)' }}>
      <span style={{ fontSize: 13, color: 'var(--gray-300)' }}>{label}</span>
      <div style={{ textAlign: 'right' }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: highlight || 'var(--white)' }}>{value}</span>
        {sub && <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{sub}</div>}
      </div>
    </div>
  );
}

// Deal-quality signal badges (from the comp engine's dealSignals). Each shows only when true; the
// tooltip carries the number behind it. Rendered next to the address on the underwriting header.
function DealSignalBadges({ signals }) {
  if (!signals) return null;
  const badges = [];
  if (signals.hot_market) {
    badges.push({ key: 'hot', label: '🔥 Hot Market', bg: 'rgba(239,68,68,0.14)', fg: '#ef4444',
      title: `${signals.hot_market_comps} recent same-subdivision comps — active investor market` });
  }
  if (signals.large_spread) {
    badges.push({ key: 'spread', label: '💰 Large Spread', bg: 'rgba(16,185,129,0.14)', fg: '#10b981',
      title: `Investor margin ${signals.investor_margin_pct}% of ARV (≥ ${signals.large_spread_min_pct}%)` });
  }
  if (signals.well_priced) {
    badges.push({ key: 'wp', label: '✓ Well Priced', bg: 'rgba(20,184,166,0.14)', fg: 'var(--teal)',
      title: `List price within ${signals.well_priced_within_pct}% of MAO` });
  }
  if (!badges.length) return null;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
      {badges.map(b => (
        <span key={b.key} title={b.title}
          style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 999,
            background: b.bg, color: b.fg, fontSize: 12, fontWeight: 700, letterSpacing: '0.01em' }}>
          {b.label}
        </span>
      ))}
    </div>
  );
}

export default function Underwriting() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [entErr, setEntErr] = useState(null);   // entitlement wall (locked / limit) on this feature
  const [loading, setLoading] = useState(true);

  const [contractOpen, setContractOpen] = useState(false);
  const [negotiationOpen, setNegotiationOpen] = useState(false);
  const [packetOpen, setPacketOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [blastOpen, setBlastOpen] = useState(false);
  const [commsRefresh, setCommsRefresh] = useState(0);

  // Comp feedback state
  const [compVotes, setCompVotes] = useState({});       // compId → 'up'|'down'
  const [compReasons, setCompReasons] = useState({});   // compId → saved reason
  const [pendingReason, setPendingReason] = useState({}); // compId → draft text
  const [compDetail, setCompDetail] = useState(null);   // comp opened in the detail modal
  const [recalculating, setRecalculating] = useState(false);
  const [refinedArv, setRefinedArv] = useState(null);
  const [excludedCount, setExcludedCount] = useState(0);
  const [recomping, setRecomping] = useState(false);   // "Re-run comps" in flight
  const [rerunningArv, setRerunningArv] = useState(false); // "Re-run with selected comps" in flight
  const [selectedComps, setSelectedComps] = useState(new Set()); // comp ids the analysis considers
  const [activeTab, setActiveTab] = useState('arv');   // which tool panel is showing
  const [actionsOpen, setActionsOpen] = useState(false); // ACTIONS dropdown
  const actionsRef = React.useRef(null);

  useEffect(() => {
    if (!actionsOpen) return;
    function onDoc(e) { if (actionsRef.current && !actionsRef.current.contains(e.target)) setActionsOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [actionsOpen]);

  // Fold a fresh underwriting payload into all the derived UI state (votes, exclusions, comp selection).
  const applyData = useCallback((d) => {
    setData(d);
    setRefinedArv(null);
    const votes = {};
    for (const c of (d.comps || [])) if (c.existing_vote) votes[c.id] = c.existing_vote;
    setCompVotes(votes);
    setExcludedCount(d.excludedByFeedback > 0 ? d.excludedByFeedback : Object.values(votes).filter(v => v === 'down').length);
    const sel = (Array.isArray(d.selectedCompIds) && d.selectedCompIds.length) ? d.selectedCompIds : (d.comps || []).map(c => c.id);
    setSelectedComps(new Set(sel.map(String)));
  }, []);

  const refetch = useCallback(async () => {
    try { const d = await apiGet(`/api/underwriting/${id}`); if (d && !d.financials_redacted) applyData(d); } catch { /* ignore */ }
  }, [id, applyData]);

  // Save an inline-edited metric (ai_arv / max_offer / repair_cost) straight from the deal page.
  const saveField = useCallback(async (field, value) => {
    try {
      const r = await fetch(`/api/deals/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ [field]: value }) });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || 'failed');
      await refetch();
    } catch (e) { alert(`Save failed: ${e.message}`); }
  }, [id, refetch]);

  const toggleComp = useCallback((cid, on) => setSelectedComps(prev => {
    const n = new Set(prev); const k = String(cid);
    if (on) n.add(k); else n.delete(k);
    return n;
  }), []);

  // Re-run the ARV analysis against only the checked comps (recomputes ARV + MAO; persists the pick).
  async function rerunWithSelected() {
    if (selectedComps.size === 0) { alert('Select at least one comp to consider.'); return; }
    setRerunningArv(true);
    try {
      const r = await fetch(`/api/underwriting/${id}/recompute-arv`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ comp_ids: [...selectedComps] }),
      });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || 'failed');
      await refetch();
    } catch (e) { alert(`Re-run failed: ${e.message}`); }
    setRerunningArv(false);
  }

  // Re-pull the comparable sales from current sold data and re-freeze the snapshot. Frozen metrics
  // (ARV/MAO/Rehab) are left untouched; this only refreshes the comp list itself.
  async function rerunComps() {
    setRecomping(true);
    try {
      const d = await apiGet(`/api/underwriting/${id}?refresh=1`);
      if (d && !d.financials_redacted) applyData(d);
    } catch { /* ignore */ }
    setRecomping(false);
  }

  useEffect(() => {
    apiGet(`/api/underwriting/${id}`)
      .then(d => {
        // Non-owner/agent viewing someone else's deal — the API redacted the financials. The full
        // underwriting page isn't for them; send them to the sales-safe Pipeline view instead.
        if (d && d.financials_redacted) { navigate(`/pipeline/deal/${id}`, { replace: true }); return; }
        applyData(d);
        setLoading(false);
      })
      .catch(err => {
        // 402 (e.g. an underwriting cap) must not flow into the data renderer — surface a friendly
        // state instead of crashing on undefined data fields.
        const s = entitlementState(err);
        if (s) setEntErr(s.error);
        setLoading(false);
      });
  }, [id, navigate, applyData]);

  async function submitVote(comp, vote, reason) {
    setRecalculating(true);
    try {
      const resp = await fetch('/api/comps/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deal_id: parseInt(id),
          comp_deal_id: comp.id,
          comp_address: comp.address,
          vote,
          reason: reason || null,
        }),
      });
      const result = await resp.json();
      if (result.updated_arv) setRefinedArv(result.updated_arv);
    } catch {}
    setRecalculating(false);
  }

  function handleThumbsUp(comp) {
    const next = compVotes[comp.id] === 'up' ? null : 'up';
    const updated = { ...compVotes };
    if (next) updated[comp.id] = next; else delete updated[comp.id];
    setCompVotes(updated);
    setExcludedCount(Object.values(updated).filter(v => v === 'down').length);
    if (next === 'up') submitVote(comp, 'up', null);
  }

  function handleThumbsDown(comp) {
    if (compVotes[comp.id] === 'down') return; // already voted down
    setCompVotes({ ...compVotes, [comp.id]: 'down' });
    setPendingReason({ ...pendingReason, [comp.id]: '' });
  }

  function handleReasonSubmit(comp) {
    const reason = pendingReason[comp.id] || '';
    setCompReasons({ ...compReasons, [comp.id]: reason });
    const updated = { ...compVotes, [comp.id]: 'down' };
    setExcludedCount(Object.values(updated).filter(v => v === 'down').length);
    submitVote(comp, 'down', reason);
  }

  if (loading) return <LoadingScreen />;
  if (entErr) {
    // 'locked' → feature not on plan; 'limit' → monthly cap reached.
    if (entErr.reason === 'locked') return <div style={{ padding: 24 }}><LockedFeaturePanel feature={entErr.feature || 'underwriting'} plan={entErr.plan} /></div>;
    return <div style={{ maxWidth: 520, margin: '48px auto', padding: 24 }}><LimitReachedNotice error={entErr} /></div>;
  }
  if (!data) return <div style={{ color: 'var(--gray-500)', padding: 40 }}>Deal not found.</div>;

  const { deal, arvAnalysis, maoAnalysis, roiProjections, aiNarrative, dealSignals, tax_record } = data;
  const displayArv = refinedArv || deal.ai_arv;
  const photos = parsePhotos(deal);

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <button onClick={() => navigate('/pipeline')} style={{ background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: 13, cursor: 'pointer', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
          Back to Pipeline
        </button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--white)', marginBottom: 4, letterSpacing: '-0.02em' }}>{deal.address}</h1>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, color: 'var(--teal)', fontWeight: 600 }}>{deal.neighborhood || deal.city || 'Austin'}, TX</span>
              <StageBadge stage={deal.stage} dealId={deal.id} deal={deal} onChanged={s => setData(d => ({ ...d, stage: s }))} />
            </div>
            <DealSignalBadges signals={dealSignals} />
            {tax_record && (tax_record.owner_name || tax_record.parcel_number || tax_record.tax_annual_amount != null) && (
              <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 8, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                {tax_record.owner_name && <span>Owner: <span style={{ color: 'var(--gray-300)', fontWeight: 600 }}>{tax_record.owner_name}</span></span>}
                {tax_record.parcel_number && <span>APN: <span style={{ color: 'var(--gray-300)', fontWeight: 600 }}>{tax_record.parcel_number}</span></span>}
                {tax_record.tax_annual_amount != null && <span>Tax: <span style={{ color: 'var(--gray-300)', fontWeight: 600 }}>${Number(tax_record.tax_annual_amount).toLocaleString()}{tax_record.tax_year ? ` (${tax_record.tax_year})` : ''}</span></span>}
              </div>
            )}
          </div>

          {/* ACTIONS dropdown — every deal action lives here instead of a button row. */}
          <div ref={actionsRef} style={{ position: 'relative', flexShrink: 0 }}>
            <button onClick={() => setActionsOpen(o => !o)}
              style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid var(--teal)', background: 'var(--teal)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              Actions <span style={{ fontSize: 10, transform: actionsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▾</span>
            </button>
            {actionsOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 200, background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 10, padding: 6, width: 250, boxShadow: '0 12px 32px rgba(26,23,20,0.16)' }}>
                {[
                  { label: '✎ Edit Deal', on: () => setEditOpen(true) },
                  { label: 'Match Buyers →', on: () => navigate(`/buyer-match/${id}`) },
                  { label: 'Generate Contract →', on: () => setContractOpen(true) },
                  ...((deal.stage === 'Tagged' || deal.stage === 'Offer Sent') ? [{ label: 'Log Agent Response →', on: () => setNegotiationOpen(true) }] : []),
                  ...(deal.stage === 'Offer Sent' ? [{ label: '✓ Mark Contract Executed →', on: () => document.getElementById('executed-upload-input').click() }] : []),
                  { label: 'Deal Packet →', on: () => navigate(`/packets/${id}`) },
                  { label: '📄 Edit Sales Packet', on: () => setPacketOpen(true) },
                  // Buyer blasting is disabled while per-agent buyer ownership and the new email
                  // provider are set up (POST /api/deals/:id/blast-buyers returns 503). Trigger
                  // removed rather than the modal, so re-enabling is a one-line revert.
                ].map((it, i) => (
                  <button key={i} onClick={() => { setActionsOpen(false); it.on(); }}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px', borderRadius: 7, border: 'none', background: 'transparent', color: 'var(--gray-300)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                    onMouseOver={e => e.currentTarget.style.background = 'var(--navy)'}
                    onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                    {it.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hidden PDF picker for the "Mark Contract Executed" action (kept mounted so the action works). */}
      <input
        id="executed-upload-input"
        type="file"
        accept="application/pdf"
        style={{ display: 'none' }}
        onChange={async (e) => {
          const file = e.target.files && e.target.files[0];
          if (!file) return;
          if (file.size > 20 * 1024 * 1024) { alert('PDF too large (>20MB).'); return; }
          try {
            const r = await fetch(`/api/contracts/${id}/upload-executed`, {
              method: 'POST', headers: { 'Content-Type': 'application/pdf' }, body: file,
            });
            const data = await r.json();
            if (!r.ok || data.error) throw new Error(data.error || `HTTP ${r.status}`);
            fetch(`/api/underwriting/${id}`).then(r => r.json()).then(d => setData(d)).catch(() => {});
          } catch (err) { alert(`Upload failed: ${err.message}`); }
          e.target.value = '';
        }}
      />

      {/* ── Material facts ────────────────────────────────────────────────────── */}
      {(() => {
        const tax = taxRecordsLink(deal);
        return (
      <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {[
          ['Square Feet', deal.sqft ? Number(deal.sqft).toLocaleString() : '—'],
          ['Lot Size',   fmtLotSize(deal) || 'Unknown'],
          ['Beds',       deal.beds ?? '—'],
          ['Baths',      deal.baths ?? '—'],
          ['Pool',       poolFact(deal)],
          ['HOA',        hoaFact(deal)],
          ['Year Built', deal.year_built || '—'],
        ].map(([k, v]) => (
          <div key={k} style={{ flex: '1 1 120px', minWidth: 108, background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>{k}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: v === 'Unknown' ? 'var(--gray-500)' : 'var(--white)' }}>{v}</div>
          </div>
        ))}
      </div>
      {tax && (
        <a href={tax.url} target="_blank" rel="noopener noreferrer"
           style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 12, fontWeight: 600, color: 'var(--accent, #B89968)', textDecoration: 'none' }}>
          {tax.label}
        </a>
      )}
      </div>
        );
      })()}

      {/* ── Photo slideshow (lazy-loaded) ─────────────────────────────────────── */}
      <PhotoSlideshow photos={photos} />

      {/* ── Money summary — ARV / MAO / Rehab are click-to-edit for the deal owner. ──────────
          No assignment-fee box here: that fee is only real once under contract (front-side price vs
          back-side sale/advertised price), so it lives on the Deals tab + Sold/Closed views instead. */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <EditableStat label="Est. ARV"        value={deal.ai_arv}     color="var(--white)" onSave={v => saveField('ai_arv', v)} />
        <EditableStat label="Max Offer (MAO)" value={deal.max_offer}  color="var(--teal)"  onSave={v => saveField('max_offer', v)} />
        <EditableStat label="Rehab"           value={deal.repair_cost} color="var(--gold)" onSave={v => saveField('repair_cost', v)} />
      </div>

      {/* ── Tools nav ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 20, borderBottom: '1px solid var(--navy-border)' }}>
        {DEAL_TABS.map(t => {
          const on = activeTab === t.key;
          return (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              style={{ padding: '10px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: on ? 'var(--teal)' : 'var(--gray-500)', borderBottom: on ? '2px solid var(--teal)' : '2px solid transparent', marginBottom: -1 }}>
              {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'arv' && (
      <>
        {/* Underwriting snapshot summary */}
        <div style={{ background: 'rgba(20, 80, 74,0.06)', border: '1px solid rgba(20, 80, 74,0.2)', borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
          <p style={{ fontSize: 13, color: 'var(--gray-100)', lineHeight: 1.65, margin: 0 }}>{aiNarrative}</p>
        </div>

        {/* ARV Analysis */}
        <Section title="ARV Analysis">
          {data.arvMode === 'teardown' && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 16, padding: '10px 12px', borderRadius: 8,
              background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.35)' }}>
              <span style={{ fontSize: 14, lineHeight: '18px' }}>🏗️</span>
              <div style={{ fontSize: 12, color: '#818cf8', fontWeight: 600, lineHeight: 1.4 }}>
                Teardown / redevelopment — ARV valued as a new build on this lot.
                <span style={{ fontWeight: 500, color: 'var(--gray-300)' }}> Comps are new construction (2015+) on similar lots{data.teardownBuildSqft ? `, sized to a ~${data.teardownBuildSqft.toLocaleString()} sqft build` : ''}.</span>
              </div>
            </div>
          )}
          {data.arvConfidence === 'low' && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 16, padding: '10px 12px', borderRadius: 8,
              background: 'rgba(224,165,38,0.12)', border: '1px solid rgba(224,165,38,0.35)' }}>
              <span style={{ fontSize: 14, lineHeight: '18px' }}>⚠️</span>
              <div style={{ fontSize: 12, color: '#E0A526', fontWeight: 600, lineHeight: 1.4 }}>
                Low-confidence ARV — the top comps disagree by {data.arvBandSpreadPct}% on $/sqft.
                <span style={{ fontWeight: 500, color: 'var(--gray-300)' }}> Hand-pick the comps below to tighten it.</span>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
            <div style={{ flex: 1, background: 'rgba(20, 80, 74,0.08)', borderRadius: 8, padding: '14px 16px', textAlign: 'center', position: 'relative' }}>
              {recalculating
                ? <div style={{ fontSize: 14, color: 'var(--teal)', fontWeight: 600 }}>Recalculating…</div>
                : <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--teal)' }}>{fmtK(displayArv)}</div>
              }
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
                {refinedArv ? 'Refined Estimate' : 'AI Estimate'}
              </div>
              {refinedArv && (
                <div style={{ fontSize: 10, color: '#4A7C59', marginTop: 3, fontWeight: 600 }}>✓ Refined by feedback</div>
              )}
            </div>
            <div style={{ flex: 1, background: 'var(--navy)', borderRadius: 8, padding: '14px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--gray-300)' }}>{fmtK(arvAnalysis.low)} – {fmtK(arvAnalysis.high)}</div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>Range ({arvAnalysis.confidence} conf.)</div>
            </div>
          </div>
          <MetricRow label="Price per sqft" value={`$${arvAnalysis.perSqft}/sqft`} />
          <MetricRow label="Comp count" value={`${arvAnalysis.compCount} sales`} highlight="var(--teal)" />
          <MetricRow label="Confidence" value={arvAnalysis.confidence} highlight={arvAnalysis.confidence === 'High' ? '#4A7C59' : '#E0A526'} />
          {excludedCount > 0 && (
            <div style={{ marginTop: 8, fontSize: 11, color: '#E0A526', fontWeight: 500 }}>
              {excludedCount} comp{excludedCount > 1 ? 's' : ''} excluded by your feedback
            </div>
          )}

          {data.comps && data.comps.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ marginBottom: 12 }}>
                <CompMap
                  subject={{ address: deal.address, lat: deal.lat, lng: deal.lng, sqft: deal.sqft }}
                  comps={data.comps}
                  votes={compVotes}
                  height={380}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Comparable Properties
                  <span style={{ marginLeft: 8, color: 'var(--teal)', textTransform: 'none', letterSpacing: 0 }}>· {selectedComps.size} selected</span>
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {data.arvSourceLabel && (
                    <span style={{ fontSize: 10, color: 'var(--gray-500)', fontWeight: 600 }}>{data.arvSourceLabel}</span>
                  )}
                  <button onClick={rerunWithSelected} disabled={rerunningArv || selectedComps.size === 0}
                    title="Recompute ARV using only the checked comps"
                    style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--teal)', background: (rerunningArv || selectedComps.size === 0) ? 'var(--navy)' : 'var(--teal)', color: (rerunningArv || selectedComps.size === 0) ? 'var(--gray-500)' : '#fff', fontSize: 12, fontWeight: 700, cursor: (rerunningArv || selectedComps.size === 0) ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
                    {rerunningArv ? 'Recomputing…' : `↻ Re-run with ${selectedComps.size} selected`}
                  </button>
                </div>
              </div>
              <div style={{ fontSize: 10, color: 'var(--gray-500)', marginBottom: 8 }}>
                Check the comps to consider, then re-run to recompute ARV (and MAO) from just those.
              </div>
              {(data.compWarnings || []).map((w, i) => (
                <div key={i} style={{ padding: '6px 10px', background: 'rgba(224, 165, 38,0.08)', border: '1px solid rgba(224, 165, 38,0.3)', borderRadius: 6, fontSize: 11, color: 'var(--gold)', marginBottom: 8 }}>{w}</div>
              ))}
              {data.comps.slice(0, 6).map((c) => {
                const vote = compVotes[c.id];
                const isDown = vote === 'down';
                const isUp = vote === 'up';
                const showReasonBox = isDown && !compReasons[c.id];
                const tierColor = c.tier === 1 ? '#4A7C59' : c.tier === 2 ? '#E0A526' : '#9C9690';
                return (
                  <div key={c.id} style={{
                    padding: '8px 0', borderBottom: '1px solid rgba(232, 227, 218, 0.7)', fontSize: 12,
                    opacity: isDown ? 0.45 : 1, transition: 'opacity 0.3s',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                      <input type="checkbox" checked={selectedComps.has(String(c.id))}
                        onChange={e => toggleComp(c.id, e.target.checked)} onClick={e => e.stopPropagation()}
                        title="Include this comp in the analysis"
                        style={{ marginTop: 3, cursor: 'pointer', flexShrink: 0, width: 15, height: 15, accentColor: 'var(--teal)' }} />
                      <div
                        style={{ flex: 1, cursor: 'pointer' }}
                        onClick={() => setCompDetail(c)}
                        title="View comp details"
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          {c.tier && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: `${tierColor}15`, color: tierColor, fontWeight: 700, letterSpacing: '0.04em' }}>TIER {c.tier}</span>}
                          {c.units > 1 && <span title={`Combined multi-unit new-build comp (${c.units} units on one lot, summed)`} style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: 'rgba(99,102,241,0.15)', color: '#818cf8', fontWeight: 700, letterSpacing: '0.04em' }}>×{c.units} UNITS</span>}
                          {c.in_arv_band && <span title="In the top $/sqft band that sets the ARV" style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: 'rgba(20,184,166,0.15)', color: 'var(--teal)', fontWeight: 700, letterSpacing: '0.04em' }}>ARV</span>}
                          {c.outlier && <span title="$/sqft is an outlier vs the comp set (below 0.80× or above 1.25× the median) — review before trusting" style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: 'rgba(224,165,38,0.18)', color: '#E0A526', fontWeight: 700, letterSpacing: '0.04em' }}>⚠ OUTLIER</span>}
                          <span style={{ color: isDown ? 'var(--gray-500)' : 'var(--gray-300)', fontWeight: 500, textDecoration: isDown ? 'line-through' : 'none', borderBottom: '1px dotted rgba(156,150,144,0.4)' }}>{c.address}</span>
                        </div>
                        <div style={{ color: 'var(--gray-500)', marginTop: 1 }}>
                          {[
                            c.beds && c.baths ? `${c.beds}bd/${c.baths}ba` : null,
                            c.sqft ? `${c.sqft.toLocaleString()} sqft` : null,
                            c.distance_mi != null ? `${c.distance_mi}mi` : null,
                            c.days_ago != null ? `${c.days_ago}d ago` : null,
                          ].filter(Boolean).join(' · ')}
                        </div>
                        {compReasons[c.id] && (
                          <div style={{ fontSize: 10, color: '#E0A526', marginTop: 3, fontStyle: 'italic' }}>
                            Excluded: {compReasons[c.id] || 'poor comparable'}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingLeft: 10 }}>
                        <div style={{ textAlign: 'right', marginRight: 4 }}>
                          <div style={{ color: 'var(--white)', fontWeight: 600 }}>{fmtK(c.sold_price)}</div>
                          {c.price_per_sqft && <div style={{ color: c.outlier ? '#E0A526' : 'var(--gray-500)', fontWeight: c.outlier ? 700 : 400 }}>${c.price_per_sqft}/sqft</div>}
                        </div>
                        {/* Thumbs up */}
                        <button
                          onClick={() => handleThumbsUp(c)}
                          title="Good comp"
                          style={{
                            background: isUp ? 'rgba(74, 124, 89,0.2)' : 'rgba(156, 150, 144,0.08)',
                            border: `1px solid ${isUp ? '#4A7C59' : 'rgba(156, 150, 144,0.2)'}`,
                            borderRadius: 6, padding: '3px 6px', cursor: 'pointer', fontSize: 12,
                            color: isUp ? '#4A7C59' : 'var(--gray-500)',
                          }}
                        >👍</button>
                        {/* Thumbs down */}
                        <button
                          onClick={() => handleThumbsDown(c)}
                          title="Poor comp"
                          style={{
                            background: isDown ? 'rgba(184, 84, 80,0.15)' : 'rgba(156, 150, 144,0.08)',
                            border: `1px solid ${isDown ? '#B85450' : 'rgba(156, 150, 144,0.2)'}`,
                            borderRadius: 6, padding: '3px 6px', cursor: 'pointer', fontSize: 12,
                            color: isDown ? '#B85450' : 'var(--gray-500)',
                          }}
                        >👎</button>
                      </div>
                    </div>
                    {/* Inline reason box */}
                    {showReasonBox && (
                      <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input
                          autoFocus
                          placeholder="Why is this a bad comp? (optional)"
                          value={pendingReason[c.id] || ''}
                          onChange={e => setPendingReason({ ...pendingReason, [c.id]: e.target.value })}
                          onKeyDown={e => e.key === 'Enter' && handleReasonSubmit(c)}
                          style={{
                            flex: 1, fontSize: 11, padding: '5px 10px',
                            background: 'var(--navy)', border: '1px solid rgba(184, 84, 80,0.4)',
                            borderRadius: 6, color: 'var(--gray-300)', outline: 'none',
                          }}
                        />
                        <button
                          onClick={() => handleReasonSubmit(c)}
                          style={{
                            fontSize: 11, padding: '5px 10px', borderRadius: 6,
                            background: 'rgba(184, 84, 80,0.15)', border: '1px solid rgba(184, 84, 80,0.4)',
                            color: '#B85450', cursor: 'pointer', fontWeight: 600,
                          }}
                        >Exclude</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Re-run comps — the deal page is frozen (no auto AI/comp refresh); this button is the
              explicit way to re-pull the latest sold comps. Frozen ARV/MAO/Rehab stay untouched. */}
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(232, 227, 218, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
              Comps are frozen from when this deal entered the pipeline. Metrics stay put — edit the deal to change ARV/MAO/Rehab.
            </span>
            <button onClick={rerunComps} disabled={recomping}
              title="Re-pull comparable sales from the latest sold data"
              style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid var(--teal)', background: recomping ? 'var(--navy)' : 'rgba(0,128,128,0.12)', color: 'var(--teal)', fontSize: 12, fontWeight: 700, cursor: recomping ? 'default' : 'pointer', whiteSpace: 'nowrap', opacity: recomping ? 0.6 : 1 }}>
              {recomping ? 'Re-running…' : '↻ Re-run comps'}
            </button>
          </div>
        </Section>
      </>
      )}

      {/* Rehab Estimator — interactive rehab builder (also shows the adjustable MAO it drives). */}
      {activeTab === 'rehab' && (
        <InteractiveDealCalc deal={deal} arv={deal.ai_arv} />
      )}

      {/* MAO Calculator — the frozen MAO tiers (Standard / Aggressive / Conservative). */}
      {activeTab === 'mao' && (
        <MaoPanel maoAnalysis={maoAnalysis} askingPrice={deal.asking_price} />
      )}

      {activeTab === 'roi' && (
        <Section title="Investor ROI Projection">
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Fix & Flip</div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
              <div style={{ flex: 1, background: 'rgba(74, 124, 89,0.08)', borderRadius: 8, padding: '14px', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#4A7C59' }}>{roiProjections.flipROI}%</div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>Projected ROI</div>
              </div>
              <div style={{ flex: 1, background: 'var(--navy)', borderRadius: 8, padding: '14px', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--white)' }}>{fmtK(roiProjections.flipProfit)}</div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>Net Profit</div>
              </div>
            </div>
            <MetricRow label="All-in Cost (purchase + repairs + holding)" value={fmtK(roiProjections.allInCost)} />
            <MetricRow label="After Repair Value" value={fmtK(deal.ai_arv)} />
          </div>

          <div style={{ borderTop: '1px solid var(--navy-border)', paddingTop: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>BRRRR / Buy & Hold</div>
            <MetricRow label="Est. Monthly Rent" value={fmtMoney(roiProjections.estimatedRent)} highlight="var(--teal)" />
            <MetricRow label="Monthly NOI" value={fmtMoney(roiProjections.monthlyNOI)} />
            <MetricRow label="75% LTV Refi Amount" value={fmtK(roiProjections.refinanceValue)} />
            <MetricRow label="Cash-on-Cash Return" value={`${roiProjections.cashOnCash}%`} highlight={roiProjections.cashOnCash >= 8 ? '#4A7C59' : '#E0A526'} />
          </div>
        </Section>
      )}

      {/* Communications & Negotiation Thread */}
      {activeTab === 'comms' && (
      <Section title={`Communications Thread${deal.negotiation_rounds > 0 ? ` · Round ${deal.negotiation_rounds}` : ''}`}>
        {deal.negotiation_rounds > 0 && (
          <div style={{ marginBottom: 14, padding: 10, background: 'rgba(20, 80, 74,0.08)', border: '1px solid rgba(20, 80, 74,0.25)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#14504A', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Negotiation Score</span>
            <NegotiationScore deal={deal} />
          </div>
        )}
        <CommunicationsTimeline dealId={id} refreshKey={commsRefresh} />
      </Section>
      )}

      {activeTab === 'docs' && (
        <DealDocuments
          dealId={id}
          onStageChange={() => {
            // An executed purchase-contract upload advanced the stage — refetch to reflect it.
            fetch(`/api/underwriting/${id}`).then(r => r.json()).then(d => setData(d)).catch(() => {});
            setCommsRefresh(k => k + 1);
          }}
        />
      )}

      {packetOpen && (
        <DealPacketModal dealId={deal.id} dealAddress={deal.address} onClose={() => setPacketOpen(false)} />
      )}

      {editOpen && (
        <DealEditModal deal={deal} onClose={() => setEditOpen(false)}
          onSaved={() => { fetch(`/api/underwriting/${id}`).then(r => r.json()).then(d => setData(d)).catch(() => {}); }} />
      )}

      {contractOpen && (
        <ContractModal
          deal={deal}
          dealId={deal.id}
          onClose={() => setContractOpen(false)}
          onGenerated={() => {
            // Refetch underwriting data to reflect updated stage
            fetch(`/api/underwriting/${id}`).then(r => r.json()).then(d => setData(d)).catch(() => {});
            setCommsRefresh(k => k + 1);
          }}
        />
      )}

      {negotiationOpen && (
        <NegotiationModal
          deal={deal}
          dealId={deal.id}
          onClose={() => setNegotiationOpen(false)}
          onSent={() => {
            // Leave the modal on its sent confirmation; just refresh behind it.
            fetch(`/api/underwriting/${id}`).then(r => r.json()).then(d => setData(d)).catch(() => {});
            setCommsRefresh(k => k + 1);
          }}
        />
      )}

      {blastOpen && (
        <MarketDealModal
          deal={deal}
          onClose={() => setBlastOpen(false)}
          onSent={() => {
            // Refresh the communications timeline so the user sees the new outbound rows immediately
            setCommsRefresh(k => k + 1);
          }}
        />
      )}

      {compDetail && (
        <CompDetailModal
          comp={compDetail}
          subject={deal ? { address: deal.address, lat: deal.lat, lng: deal.lng, sqft: deal.sqft } : null}
          onClose={() => setCompDetail(null)}
        />
      )}
    </div>
  );
}

// Click-to-edit money stat (ARV / MAO / Rehab). Shows the value with a pencil; clicking either the
// value or the pencil turns it into an inline input that PUTs the field on save (Enter) / cancels (Esc).
function EditableStat({ label, value, color, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const [saving, setSaving] = useState(false);
  const start = () => { setVal(value != null ? String(value) : ''); setEditing(true); };
  const commit = async () => {
    setSaving(true);
    await onSave(val === '' ? null : Number(val));
    setSaving(false); setEditing(false);
  };
  return (
    <div style={{ flex: '1 1 160px', background: 'var(--navy-card)', border: `1px solid ${editing ? 'var(--teal)' : 'var(--navy-border)'}`, borderRadius: 10, padding: '14px 18px' }}>
      <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{label}</span>
        {!editing && <span onClick={start} title={`Edit ${label}`} style={{ cursor: 'pointer', color: 'var(--teal)', fontWeight: 700, fontSize: 12 }}>✎</span>}
      </div>
      {editing ? (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ color: 'var(--gray-500)', fontSize: 16 }}>$</span>
          <input autoFocus type="number" value={val} onChange={e => setVal(e.target.value)} disabled={saving}
            onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
            style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid var(--teal)', background: 'var(--navy)', color: 'var(--white)', fontSize: 18, fontWeight: 700 }} />
          <button onClick={commit} disabled={saving} title="Save"
            style={{ padding: '5px 9px', borderRadius: 6, border: 'none', background: 'var(--teal)', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>✓</button>
        </div>
      ) : (
        <div onClick={start} title="Click to edit" style={{ fontSize: 22, fontWeight: 700, color, cursor: 'pointer' }}>{fmtK(value)}</div>
      )}
    </div>
  );
}

// Lazy-loaded photo slideshow. Only the current image is rendered at full size (loading="lazy"), and
// the thumbnail strip images are lazy too — so a deal with many MLS photos doesn't fetch them all up
// front. Falls back to a placeholder when the deal has no photos.
function PhotoSlideshow({ photos }) {
  const [idx, setIdx] = useState(0);
  if (!photos || photos.length === 0) {
    return (
      <div style={{ height: 220, borderRadius: 12, border: '1px dashed var(--navy-border)', background: 'var(--navy-card)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gray-500)', fontSize: 13, marginBottom: 18 }}>
        No photos uploaded or captured for this property yet.
      </div>
    );
  }
  const cur = Math.min(idx, photos.length - 1);
  const go = (d) => setIdx(i => { const n = (i + d + photos.length) % photos.length; return n; });
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--navy-border)', background: 'var(--navy)' }}>
        <img key={photos[cur]} src={proxyPhoto(photos[cur])} alt="" loading="lazy"
          style={{ width: '100%', height: 420, objectFit: 'cover', display: 'block' }} />
        {photos.length > 1 && (
          <>
            <button onClick={() => go(-1)} aria-label="Previous photo"
              style={slideNav('left')}>‹</button>
            <button onClick={() => go(1)} aria-label="Next photo"
              style={slideNav('right')}>›</button>
            <div style={{ position: 'absolute', bottom: 10, right: 12, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20 }}>
              {cur + 1} / {photos.length}
            </div>
          </>
        )}
      </div>
      {photos.length > 1 && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8, overflowX: 'auto', paddingBottom: 2 }}>
          {photos.map((p, i) => (
            <img key={p} src={proxyPhoto(p)} alt="" loading="lazy"
              onClick={() => setIdx(i)}
              style={{ width: 74, height: 54, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', flexShrink: 0, border: i === cur ? '2px solid var(--teal)' : '2px solid transparent', opacity: i === cur ? 1 : 0.65 }} />
          ))}
        </div>
      )}
    </div>
  );
}
function slideNav(side) {
  return {
    position: 'absolute', top: '50%', [side]: 10, transform: 'translateY(-50%)',
    width: 38, height: 38, borderRadius: '50%', border: 'none', cursor: 'pointer',
    background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 22, lineHeight: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
}

// MAO Calculator panel — the frozen MAO tiers carried over from underwriting. Standard = 80% rule.
function MaoPanel({ maoAnalysis, askingPrice }) {
  const m = maoAnalysis || {};
  const tiers = [
    { label: 'Conservative', sub: '75% rule', value: m.conservative, color: '#B85450' },
    { label: 'Standard',     sub: '80% rule', value: m.standard,     color: 'var(--teal)', primary: true },
    { label: 'Aggressive',   sub: '85% rule', value: m.aggressive,   color: '#E0A526' },
  ];
  return (
    <Section title="MAO Calculator">
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 18 }}>
        {tiers.map(t => (
          <div key={t.label} style={{ flex: '1 1 180px', background: t.primary ? 'rgba(0,128,128,0.10)' : 'var(--navy)', border: `1px solid ${t.primary ? 'var(--teal)' : 'var(--navy-border)'}`, borderRadius: 10, padding: '16px 18px', textAlign: 'center' }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: t.color }}>{fmtK(t.value)}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--white)', marginTop: 4 }}>{t.label}</div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{t.sub}</div>
          </div>
        ))}
      </div>
      <MetricRow label="Recommended Max Offer" value={fmtK(m.recommended ?? m.standard)} highlight="var(--teal)" />
      {askingPrice != null && <MetricRow label="Asking Price" value={fmtK(askingPrice)} />}
      {m.savedMaoPct != null && (
        <div style={{ marginTop: 10, fontSize: 11, color: 'var(--gray-500)' }}>Saved MAO preference for this zip: {m.savedMaoPct}%</div>
      )}
    </Section>
  );
}

function NegotiationScore({ deal }) {
  const rounds = deal.negotiation_rounds || 0;
  const lastReceived = deal.last_offer_received;
  const original = deal.asking_price || 0;
  const mao = deal.max_offer || 0;

  let trajectory = 'Neutral', color = '#E0A526';
  if (lastReceived && original && mao) {
    const startGap = original - mao;
    const currentGap = lastReceived - mao;
    if (currentGap < startGap * 0.5) { trajectory = 'Approaching MAO'; color = '#4A7C59'; }
    else if (currentGap > startGap * 0.9) { trajectory = 'Stalled'; color = '#B85450'; }
    else { trajectory = 'Moving toward MAO'; color = '#E0A526'; }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 12, color: 'var(--gray-300)' }}>
      <span><strong style={{ color: '#14504A' }}>{rounds}</strong> round{rounds !== 1 ? 's' : ''}</span>
      <span style={{ color: 'var(--gray-500)' }}>·</span>
      <span style={{ color, fontWeight: 600 }}>{trajectory}</span>
      {lastReceived && (
        <>
          <span style={{ color: 'var(--gray-500)' }}>·</span>
          <span>Last counter: <strong style={{ color: 'var(--white)' }}>${Math.round(lastReceived / 1000)}k</strong></span>
        </>
      )}
    </div>
  );
}

function LoadingScreen() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: 16 }}>
      <div style={{ width: 48, height: 48, border: '3px solid var(--navy-border)', borderTopColor: 'var(--teal)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <div style={{ color: 'var(--gray-500)', fontSize: 14 }}>Running AI underwriting analysis...</div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
